// ABOUTME: Autocomplete listbox that renders only the visible options through react-window
// ABOUTME: Asks the consumer for the next page while the user scrolls towards the end of the list

import { CircularProgress } from '@ds/coolshop';
import {
    Children,
    cloneElement,
    createContext,
    type CSSProperties,
    forwardRef,
    type HTMLAttributes,
    isValidElement,
    type ReactElement,
    type ReactNode,
    useContext,
} from 'react';
import { List, type RowComponentProps } from 'react-window';

// Every option renders on a single ellipsized line, so all the rows share this height.
export const OPTION_ROW_HEIGHT = 40;
const MAX_VISIBLE_ROWS = 7;
// Ask for the next page a few rows before the end of the loaded ones, so it is usually already
// there by the time the user scrolls down to it.
const LOAD_MORE_ROW_THRESHOLD = 5;

export type ListboxPaging = {
    // Whether the server has more options after the ones currently rendered.
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
};

const NO_PAGING: ListboxPaging = {
    hasMore: false,
    loadingMore: false,
    onLoadMore: () => {},
};

const ListboxPagingContext = createContext<ListboxPaging>(NO_PAGING);

// The listbox is rendered in a portal, out of the Autocomplete's DOM subtree, so the paging
// callbacks reach it through a context instead of props.
export const ListboxPagingProvider = ListboxPagingContext.Provider;

type OptionRowProps = { options: ReactNode[] };

function OptionRow(props: RowComponentProps<OptionRowProps>) {
    const { index, style, options } = props;
    const option = options[index];

    // The extra row past the last loaded option is the placeholder for the page being fetched.
    if (!isValidElement(option)) {
        return (
            <div
                style={{
                    ...style,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <CircularProgress size="small" />
            </div>
        );
    }

    const optionElement = option as ReactElement<{ style?: CSSProperties }>;

    // minHeight is reset because the option styling of the Autocomplete asks for 48px on small
    // screens, which would overflow the fixed height the virtualized row is positioned with.
    return cloneElement(optionElement, {
        style: { ...optionElement.props.style, ...style, minHeight: 0 },
    });
}

export const VirtualizedAutocompleteListbox = forwardRef<
    HTMLDivElement,
    HTMLAttributes<HTMLElement>
>((props, ref) => {
    const { children, style, ...other } = props;

    const { hasMore, loadingMore, onLoadMore } = useContext(ListboxPagingContext);

    const options = Children.toArray(children);
    const rowCount = options.length + (hasMore ? 1 : 0);
    const height = Math.min(rowCount, MAX_VISIBLE_ROWS) * OPTION_ROW_HEIGHT;

    return (
        // The listbox props of the Autocomplete stay on this element so that it keeps being the
        // one with role="listbox": the highlight bookkeeping of the Autocomplete resets the
        // scrollTop of the element it finds that role on whenever the option list changes, which
        // would send the user back to the top of the list on every loaded page. Its own scrolling
        // styles are dropped for the same reason, the inner list is the only scroll container.
        <div
            ref={ref}
            {...other}
            style={{ ...style, padding: 0, overflow: 'hidden', maxHeight: 'none' }}
        >
            <List
                role="presentation"
                rowComponent={OptionRow}
                rowCount={rowCount}
                rowHeight={OPTION_ROW_HEIGHT}
                rowProps={{ options }}
                overscanCount={5}
                style={{ height, maxHeight: 'none' }}
                onRowsRendered={({ stopIndex }) => {
                    if (
                        hasMore &&
                        !loadingMore &&
                        stopIndex >= options.length - LOAD_MORE_ROW_THRESHOLD
                    ) {
                        onLoadMore();
                    }
                }}
            />
        </div>
    );
});
